import { fetch } from '@/common/request';

// 分页结果
export interface PageResult<T> {
  records: T[];
  total: number;
  size: number;
  current: number;
  totalPages?: number;
}

// 知识库
export interface KnowledgeBaseVo {
  id: number;
  name: string;
  description?: string;
  kbType?: string;
  embeddingModel?: string;
  chunkSize?: number;
  chunkOverlap?: number;
  retrievalTopK?: number;
  retrievalThreshold?: number;
  createTime?: string;
  updateTime?: string;
}

export interface KnowledgeBaseCreateReq {
  name: string;
  description?: string;
  kbType?: string;
  embeddingModel?: string;
  chunkSize?: number;
  chunkOverlap?: number;
  retrievalTopK?: number;
  retrievalThreshold?: number;
}

export interface KnowledgeBaseUpdateReq extends KnowledgeBaseCreateReq {
  id: number;
}

export interface KnowledgeBaseListReq {
  keyword?: string;
}

// 条目（item，取代原 document）
export interface KnowledgeItemVo {
  id: number;
  kbId: number;
  name: string;
  format?: string;
  kind?: string;        // 'rag' | 'config'
  fileSize?: number;
  status?: number;
  chunkCount?: number;
  errorMessage?: string;
  createdBy?: string;
  createdAt?: string;
  processedAt?: string;
  rawContent?: string;  // 条目原文内容
}

export interface ItemUploadReq {
  kbId: number;
  url: string;
  createdBy?: string;
}

export interface ItemListReq {
  kbId: number;
}

// ETL 任务
export interface KnowledgeEtlTaskVo {
  id: number;
  kbId: number;
  taskType?: string;
  taskStatus?: number;
  totalCount?: number;
  successCount?: number;
  failCount?: number;
  taskConfig?: string;
  taskLog?: string;
  startedAt?: string;
  finishedAt?: string;
  createdBy?: string;
  createdAt?: string;
}

export interface EtlTaskStartReq {
  kbId: number;
  createdBy?: string;
}

export interface EtlTaskListReq {
  kbId: number;
}

// 测试记录
export interface KnowledgeTestRecordVo {
  id: number;
  kbId: number;
  query: string;
  expectedAnswer?: string;
  retrievedChunks?: string;
  relevanceScore?: number;
  accuracyScore?: number;
  createdBy?: string;
  createdAt?: string;
}

export interface KnowledgeTestReq {
  kbId: number;
  query: string;
  expectedAnswer?: string;
  createdBy?: string;
}

// 统计
export interface StatsOverviewVo {
  docCount: number;
  chunkCount: number;
  queryCount: number;
  avgRelevance: number;
  hitRate: number;
}

export interface KnowledgeStatsTrendReq {
  kbId: number;
  days?: number;
}

export interface KnowledgeStatsVo {
  id: number;
  kbId: number;
  statDate: string;
  queryCount: number;
  avgRelevance: number;
  hitRate: number;
}

// 知识库 API
export function createKnowledgeBase(params: KnowledgeBaseCreateReq): Promise<number> {
  return fetch(
    '/api/config/knowledge/base/create',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function updateKnowledgeBase(params: KnowledgeBaseUpdateReq): Promise<void> {
  return fetch(
    '/api/config/knowledge/base/update',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function deleteKnowledgeBase(id: number): Promise<void> {
  return fetch(
    '/api/config/knowledge/base/delete',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function getKnowledgeBaseDetail(id: number): Promise<KnowledgeBaseVo> {
  return fetch(
    '/api/config/knowledge/base/detail',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function getKnowledgeBaseList(params?: KnowledgeBaseListReq): Promise<KnowledgeBaseVo[]> {
  return fetch(
    '/api/config/knowledge/base/list',
    {
      method: 'POST',
      body: JSON.stringify(params || {}),
    },
    true
  );
}

// 条目 API
export function uploadItem(params: ItemUploadReq): Promise<number> {
  return fetch(
    '/api/config/knowledge/item/upload',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function importItemUrl(params: ItemUploadReq): Promise<number> {
  return fetch(
    '/api/config/knowledge/item/importUrl',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export interface AddTextReq {
  kbId: number;
  content: string;
  name?: string;
  createdBy?: string;
  kind?: string;
}

export interface AddTextResult {
  itemId: number;
  chunkCount: number;
  name: string;
  kind?: string;
}

/**
 * 添加文本内容到知识库
 */
export function addTextContent(params: AddTextReq): Promise<AddTextResult> {
  return fetch(
    '/api/config/knowledge/item/addText',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function deleteItem(id: number): Promise<void> {
  return fetch(
    '/api/config/knowledge/item/delete',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export interface UpdateItemReq {
  id: number;
  name?: string;
  content: string;
}

export function updateItem(params: UpdateItemReq): Promise<void> {
  return fetch(
    '/api/config/knowledge/item/update',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function getItemDetail(id: number): Promise<KnowledgeItemVo> {
  return fetch(
    '/api/config/knowledge/item/detail',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function getItemList(kbId: number): Promise<KnowledgeItemVo[]> {
  return fetch(
    '/api/config/knowledge/item/list',
    {
      method: 'POST',
      body: JSON.stringify({ kbId }),
    },
    true
  );
}

// Chunk
export interface KnowledgeChunkVo {
  id: number;
  kbId: number;
  itemId: number;
  chunkIndex: number;
  content: string;
  embeddingStatus?: number;
  embeddingModel?: string;
  embeddingDimensions?: number;
  createdAt?: string;
}

export interface ChunkListReq {
  itemId: number;
  pageNum: number;
  pageSize: number;
}

export function getChunkList(params: ChunkListReq): Promise<PageResult<KnowledgeChunkVo>> {
  return fetch(
    '/api/config/knowledge/chunk/list',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

// ETL 任务 API
export function startEtlTask(params: EtlTaskStartReq): Promise<number> {
  return fetch(
    '/api/config/knowledge/etl/start',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function getEtlTaskProgress(id: number): Promise<KnowledgeEtlTaskVo> {
  return fetch(
    '/api/config/knowledge/etl/progress',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function cancelEtlTask(id: number): Promise<void> {
  return fetch(
    '/api/config/knowledge/etl/cancel',
    {
      method: 'POST',
      body: JSON.stringify({ id }),
    },
    true
  );
}

export function getEtlTaskList(kbId: number): Promise<KnowledgeEtlTaskVo[]> {
  return fetch(
    '/api/config/knowledge/etl/list',
    {
      method: 'POST',
      body: JSON.stringify({ kbId }),
    },
    true
  );
}

// 测试 API
export function executeKnowledgeTest(params: KnowledgeTestReq): Promise<KnowledgeTestRecordVo> {
  return fetch(
    '/api/config/knowledge/test/execute',
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}

export function getTestRecords(kbId: number): Promise<KnowledgeTestRecordVo[]> {
  return fetch(
    '/api/config/knowledge/test/records',
    {
      method: 'POST',
      body: JSON.stringify({ kbId }),
    },
    true
  );
}

export function getStatsOverview(kbId: number): Promise<StatsOverviewVo> {
  return fetch(
    '/api/config/knowledge/test/overview',
    {
      method: 'POST',
      body: JSON.stringify({ id: kbId }),
    },
    true
  );
}

export function getStatsTrend(kbId: number, days?: number): Promise<KnowledgeStatsVo[]> {
  return fetch(
    '/api/config/knowledge/test/trend',
    {
      method: 'POST',
      body: JSON.stringify({ kbId, days }),
    },
    true
  );
}

export const KB_TYPE_OPTIONS = [
  { label: '文档库', value: 'document' },
  { label: 'Web爬虫', value: 'web_crawler' },
];

export const EMBEDDING_MODEL_OPTIONS = [
  { label: 'text-embedding-v4 (推荐)', value: 'text-embedding-v4' },
  { label: 'text-embedding-v3', value: 'text-embedding-v3' },
  { label: 'DashScope Embedding', value: 'dashscope-embedding' },
  { label: 'OpenAI Embedding', value: 'openai-embedding' },
];

export const ITEM_STATUS_OPTIONS = [
  { label: '待处理', value: 0, color: 'default' },
  { label: '处理中', value: 1, color: 'blue' },
  { label: '已完成', value: 2, color: 'green' },
  { label: '失败', value: 3, color: 'red' },
];

export const ITEM_KIND_OPTIONS = [
  { label: 'RAG文档', value: 'rag', color: 'blue' },
  { label: '配置文档', value: 'config', color: 'green' },
];

export const ETL_STATUS_OPTIONS = [
  { label: '待执行', value: 0, color: 'default' },
  { label: '执行中', value: 1, color: 'blue' },
  { label: '已完成', value: 2, color: 'green' },
  { label: '失败', value: 3, color: 'red' },
  { label: '已取消', value: 4, color: 'orange' },
];
